/** Shapes of the /organisation API (PT-119) and the codes its refusals carry. */

export type OrganisationRole = "gestionnaire" | "membre";
/** ``editeur``: MAGE-X itself, whose médecins review their whole speciality (PT-119). */
export type OrganisationType = "soins" | "industriel" | "editeur";

export interface Organisation {
  id: string;
  type: OrganisationType;
  specialite: string | null;
  raison_sociale: string;
  siret: string | null;
  numero_tva: string | null;
  prenom: string | null;
  nom: string | null;
  adresse: string;
  complement_adresse: string | null;
  code_postal: string;
  ville: string;
  pays: string;
  email_facturation: string;
}

export interface OrganisationMember {
  id: string;
  email: string;
  prenom: string | null;
  nom: string | null;
  role: OrganisationRole;
  is_me: boolean;
  /** MAGE-X's own organisation only (PT-137): the speciality on the member's account… */
  specialite?: string | null;
  /** …and those an admin granted them beyond it, where they correct as in their own. */
  granted_specialites?: string[];
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: OrganisationRole;
  created_at: string;
}

export interface InvitationForMe {
  id: string;
  organisation_id: string;
  raison_sociale: string;
  type: OrganisationType;
  role: OrganisationRole;
  invited_by: string;
}

export interface OrganisationSummary {
  id: string;
  raison_sociale: string;
  type: OrganisationType;
  role: OrganisationRole;
  active: boolean;
  /** Working there changes what the account reads: an industriel, for anyone but a manufacturer's reader. */
  narrows_session: boolean;
}

export interface OrganisationsResponse {
  organisations: OrganisationSummary[];
  invitations: InvitationForMe[];
}

export interface OrganisationDetail {
  organisation: Organisation;
  role: OrganisationRole;
  members: OrganisationMember[];
  invitations: PendingInvitation[];
}

export function isOrganisationsResponse(
  res: unknown,
): res is OrganisationsResponse {
  return (
    typeof res === "object" &&
    res !== null &&
    Array.isArray((res as OrganisationsResponse).organisations)
  );
}

export function isOrganisationDetail(res: unknown): res is OrganisationDetail {
  return (
    typeof res === "object" &&
    res !== null &&
    Array.isArray((res as OrganisationDetail).members)
  );
}

// The codes the API answers with; anything else falls back to a generic message.
export const ERROR_CODES = [
  "siret_invalide",
  "code_postal_invalide",
  "siret_inconnu",
  "registre_indisponible",
  "raison_sociale_requise",
  "prenom_nom_requis",
  "lecture_seule",
  "editeur_reserve_admin",
  "compte_demo",
  "conflit",
  "deja_membre",
  "deja_invite",
  "dernier_gestionnaire",
  "invitation_introuvable",
  "organisation_introuvable",
  "membre_introuvable",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** What a failed call answers: a code from the API, a 422 list, or the client's own envelope. */
interface ApiFailure {
  detail?: string | { msg?: string }[];
  error?: string;
}

/** The API's code, wherever it sits: a plain ``detail`` or a 422 item's message. */
export function errorCode(res: unknown): ErrorCode | null {
  const detail = (res as ApiFailure | null)?.detail;
  const texts =
    typeof detail === "string"
      ? [detail]
      : Array.isArray(detail)
        ? detail.map((item) => item.msg ?? "")
        : [];
  for (const text of texts) {
    const code = ERROR_CODES.find((candidate) => text.includes(candidate));
    if (code) return code;
  }
  return null;
}

/** True for a 422 the API answered field by field. */
export function isFieldValidationFailure(res: unknown): boolean {
  return Array.isArray((res as ApiFailure | null)?.detail);
}

export function personName(person: {
  prenom: string | null;
  nom: string | null;
  email: string;
}): string {
  const name = `${person.prenom ?? ""} ${person.nom ?? ""}`.trim();
  return name || person.email;
}
